import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import useApp from "../../store/contexts/AppContext";
import RoomJoinRequestsMessage from "./RoomJoinRequestsMessage";
import FormatNumberCounter from "../../utils/FormatNumberCounter";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MAX_REQUESTS = 10;

export default function RoomJoinRequestsModal({
  roomJoinRequestsOpen,
  setRoomJoinRequestsOpen,
}) {
  const {
    appState: { waitingRoomParticipantRequests },
  } = useApp();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    if (waitingRoomParticipantRequests.length > 0) {
      setRoomJoinRequestsOpen(true);
    } else {
      setRoomJoinRequestsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingRoomParticipantRequests]);

  const count = waitingRoomParticipantRequests.length;
  const visibleRequests = waitingRoomParticipantRequests.slice(0, MAX_REQUESTS);
  const formattedCount = FormatNumberCounter(count);
  const isAbbreviated = formattedCount !== count.toString();

  return (
    <Dialog
      open={roomJoinRequestsOpen}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => setRoomJoinRequestsOpen(false)}
      aria-describedby="room-join-requests-dialog"
      maxWidth="xs"
      fullWidth
      fullScreen={false}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? "12px" : "16px",
          background: isDark ? theme.palette.background.paper : "#ffffff",
          boxShadow: isDark
            ? "0 24px 48px rgba(0,0,0,0.6)"
            : "0 24px 48px rgba(0,0,0,0.15)",
          overflow: "hidden",
          // On mobile: sit near bottom; on desktop: centered
          ...(isMobile && {
            position: "fixed",
            bottom: 16,
            left: 16,
            right: 16,
            margin: 0,
            width: "calc(100% - 32px)",
            maxWidth: "100%",
          }),
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            px: isMobile ? "16px" : "20px",
            py: isMobile ? "12px" : "14px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.75)})`,
          }}
        >
          <Box
            sx={{
              width: isMobile ? 34 : 38,
              height: isMobile ? 34 : 38,
              borderRadius: "10px",
              background: alpha("#fff", 0.2),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <MeetingRoomIcon sx={{ color: "#fff", fontSize: isMobile ? "18px" : "20px" }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              Room Join Requests
            </Typography>
            {count > 0 && (
              <Tooltip
                title={isAbbreviated ? `${count.toLocaleString()} pending requests` : ""}
                placement="bottom-start"
                disableHoverListener={!isAbbreviated}
                arrow
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: alpha("#fff", 0.85),
                    mt: "2px",
                    display: "inline-block",
                  }}
                >
                  {formattedCount} pending {count === 1 ? "request" : "requests"}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </DialogTitle>

      {/* Content — ~2 cards visible, scrollable */}
      <DialogContent
        sx={{
          p: isMobile ? "10px" : "12px",
          pt: isMobile ? "10px !important" : "12px !important",
          // ~2 rows visible; on mobile a touch smaller
          maxHeight: isMobile ? "148px" : "160px",
          overflowY: "auto",
          background: isDark ? theme.palette.background.paper : "#ffffff",

          "&::-webkit-scrollbar": { width: "5px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? alpha("#fff", 0.18) : alpha("#000", 0.13),
            borderRadius: "10px",
          },
        }}
      >
        {count > 0 ? (
          visibleRequests.map((request) => (
            <RoomJoinRequestsMessage request={request} key={request.id} />
          ))
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: "24px",
              fontSize: "14px",
              color: isDark ? alpha("#fff", 0.4) : alpha("#000", 0.4),
            }}
          >
            No pending requests
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: isMobile ? "12px" : "14px",
          py: isMobile ? "8px" : "10px",
          borderTop: `1px solid ${isDark ? alpha("#fff", 0.07) : alpha("#000", 0.07)}`,
          background: isDark ? alpha("#fff", 0.02) : alpha("#000", 0.02),
        }}
      >
        <Button
          onClick={() => setRoomJoinRequestsOpen(false)}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            color: theme.palette.primary.main,
            minHeight: "unset",
            minWidth: "unset",
            px: "14px",
            py: "6px",
            "&:hover": {
              background: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
}
